import { Chip, Divider, Stack, Typography } from '@mui/material';
import { DateTime, Interval } from 'luxon';
import { BikeleasingContractDetailsDTO, BikeleasingContractInspectionDTO } from 'probonio-shared-ui/api';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { InfoAccordion } from '../../../component/infoAccordion/InfoAccordion';
import { fontWeights } from '../../../theme';

const UPCOMING_CODE_WINDOW_DAYS = 14;

enum InspectionStatus {
  PERFORMED = 'PERFORMED',
  ACTIVE = 'ACTIVE',
  UPCOMING = 'UPCOMING',
  EXPIRED = 'EXPIRED',
}

function getInspectionStatus(inspection: BikeleasingContractInspectionDTO): InspectionStatus {
  if (inspection.inspectionPerformed) {
    return InspectionStatus.PERFORMED;
  }

  const now = DateTime.now();
  const from = inspection.inspectionFrom ? DateTime.fromISO(inspection.inspectionFrom) : undefined;
  const to = inspection.inspectionTo ? DateTime.fromISO(inspection.inspectionTo) : undefined;

  const isOngoing = from && to ? Interval.fromDateTimes(from, to).contains(now) : false;
  const startsWithinWindow = from ? Interval.fromDateTimes(now, now.plus({ days: UPCOMING_CODE_WINDOW_DAYS })).contains(from) : false;

  if (isOngoing || startsWithinWindow) {
    return InspectionStatus.ACTIVE;
  }

  const hasExpired = to ? now > to : from ? now > from : false;

  return hasExpired ? InspectionStatus.EXPIRED : InspectionStatus.UPCOMING;
}

function getInspectionStatusColor(status: InspectionStatus) {
  switch (status) {
    case InspectionStatus.PERFORMED:
      return 'default';
    case InspectionStatus.ACTIVE:
      return 'success';
    case InspectionStatus.UPCOMING:
      return 'info';
    case InspectionStatus.EXPIRED:
      return 'warning';
  }
}

function getInspectionDateRange(inspection: BikeleasingContractInspectionDTO, renderDate: (value: string | undefined) => string): string {
  const from = inspection.inspectionFrom ? renderDate(inspection.inspectionFrom) : undefined;
  const to = inspection.inspectionTo ? renderDate(inspection.inspectionTo) : undefined;
  if (from && to) {
    return `${from} - ${to}`;
  }
  return from ?? to ?? '-';
}

interface InspectionDetailsProps {
  label: string;
  value: string;
}

const InspectionDetails: React.FC<InspectionDetailsProps> = ({ label, value }) => {
  return (
    <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
      <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: fontWeights.semiBold }}>
        {label}:
      </Typography>
      <Typography variant="body1" sx={{ color: 'text.secondary' }}>
        {value}
      </Typography>
    </Stack>
  );
};

interface InspectionItemProps {
  inspection: BikeleasingContractInspectionDTO;
}

const InspectionItem: React.FC<InspectionItemProps> = ({ inspection }) => {
  const { t } = useTranslation('extensions');
  const status = getInspectionStatus(inspection);

  const renderDate = useCallback(
    (value: string | undefined): string => (value ? t('common:date', { date: DateTime.fromISO(value).toJSDate() }) : ''),
    [t],
  );

  return (
    <Stack spacing={0.5}>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="caption">{t('bikeleasing_v2.contract.inspection.header', { number: inspection.inspectionNumber })}</Typography>
        <Chip label={t(`bikeleasing_v2.contract.inspection.status.${status}`)} color={getInspectionStatusColor(status)} />
      </Stack>
      <Stack spacing={0.5}>
        {status === InspectionStatus.PERFORMED && inspection.inspectionPerformedOn ? (
          <InspectionDetails
            label={t('bikeleasing_v2.contract.inspection.performedOn')}
            value={renderDate(inspection.inspectionPerformedOn)}
          />
        ) : (
          <InspectionDetails
            label={t('bikeleasing_v2.contract.inspection.period')}
            value={getInspectionDateRange(inspection, renderDate)}
          />
        )}

        {status === InspectionStatus.ACTIVE && (
          <InspectionDetails label={t('bikeleasing_v2.contract.inspection.code')} value={inspection.inspectionCode ?? '-'} />
        )}
      </Stack>
    </Stack>
  );
};

interface InspectionSectionProps {
  contract: BikeleasingContractDetailsDTO;
}

export const InspectionSection: React.FC<InspectionSectionProps> = ({ contract }) => {
  const { t } = useTranslation('extensions');
  const { inspections } = contract;

  return (
    <InfoAccordion title={t('bikeleasing_v2.contract.section.inspection')}>
      <Stack sx={{ mx: 1, gap: 1 }} divider={<Divider />}>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          {t('bikeleasing_v2.contract.inspectionInfo')}
        </Typography>
        <Stack spacing={1} sx={{ flex: 1, pb: 1 }} divider={<Divider />}>
          {inspections.map((inspection, index) => (
            <InspectionItem key={inspection.inspectionId ?? inspection.inspectionNumber ?? index} inspection={inspection} />
          ))}
        </Stack>
      </Stack>
    </InfoAccordion>
  );
};
